import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, IconButton, TextField, Button, Tooltip } from "@mui/material";
import TableComponent from "../../common/components/NewReactTable/index.jsx";
import { toastr } from "react-redux-toastr";
import "./aiSearch.scss";
import CircularProgress from "@mui/material/CircularProgress";
import { methods, request } from "../../../utils/http-common.js";
import { generateFileName } from "../../../utils/exportUtil.js";
import { APP_ICONS } from "../../../constant.js";
import SearchIcon from '@mui/icons-material/Search';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import HistoryIcon from '@mui/icons-material/History';
import SaveQueryDialog from './SaveQueryDialog.jsx';
import QueryHistoryDialog from './QueryHistoryDialog.jsx';
import { numberWithCommas } from "../../../utils/numberUtils.js";
import ChartRenderer from "../../common/components/ChartRenderer/ChartRenderer.jsx";

const moduleID = "home"

const AISearch = () => {
  // ========================================
  // STATE DECLARATIONS
  // ========================================
  
  // Core search states
  const [tableHeaderData, setTableHeaderData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // UI state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  
  // Save button state
  const [saveButtonVisible, setSaveButtonVisible] = useState(false);
  const [saveButtonTooltip, setSaveButtonTooltip] = useState("");
  
  // Query management states
  const [previousQueries, setPreviousQueries] = useState([]);
  const [isFetchingQueries, setIsFetchingQueries] = useState(false);
  const [selectedPreviousQuery, setSelectedPreviousQuery] = useState(null);
  
  // Consolidated search query state object
  const [aiSearchQuery, setAiSearchQuery] = useState({
      user_query: "",
      is_new_query: true,
      query_name: "",
      generated_query: "",
      collection_name: "",
      module_id: moduleID,
      timestamp: "",
      user_email: "",
      chart_suggestions: [],
      selected_chart_config: null
  });
  
  // Refs
  const aitableRef = useRef(null);

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================
  
  const changeKeysOfHeader = (data) => {
      if (data?.length) {
          const tableElement = document.getElementsByClassName("table-region")[0];
          const minColumnWidth = 150;
          const calculatedWidth = tableElement ? tableElement.clientWidth * (1 / data.length) : minColumnWidth;
          
          const headerList = data.map((item) => {
              return {
                  ...item,
                  Header: () => <div>{item.label}</div>,
                  id: item.key,
                  Cell: (props) => {
                      const rowData = props.row.original;
                      return (
                          <div className="flex ai-center jc-space-evenly text-capitalize">
                              <div style={{overflow:'auto', wordWrap:'break-word'}}>
                                  {typeof rowData[item.key] === 'number' ? numberWithCommas(rowData[item.key]) :
                                   typeof rowData[item.key] === 'string' ? rowData[item.key] : 
                                   JSON.stringify(rowData[item.key])}
                              </div>
                          </div>
                      );
                  },
              };
          });
          return headerList;
      }
      return [];
  };

  // ========================================
  // API CALL FUNCTIONS
  // ========================================
  
  const fetchPreviousQueries = async () => {
      setIsFetchingQueries(true);
      const payload = { module_id: moduleID };

      try {
          const [error, response] = await request('redirect/ai/fetch-logs/', methods.POST, payload);
          if (response) {
              setPreviousQueries(Array.isArray(response.data) ? response.data : []);
          }
          if (error) {
              toastr.error(error.message || 'Error fetching previous queries');
          }
      } finally {
          setIsFetchingQueries(false);
      }
  };

  const handleDownloadExcel = async () => {
      const data = {
          query: aiSearchQuery.generated_query,
          collection_name: aiSearchQuery.collection_name,
      };

      const [error, response] = await request('redirect/ai/export-excel/', methods.POST, data, {
          responseType: 'blob',
      });

      if (error) {
          toastr.error(error.message || 'Download failed');
          return;
      }

      try {
          const blob = response.data;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const contentDisposition = response.headers['content-disposition'] || '';
          const filenamePart = contentDisposition.includes('=')
              ? contentDisposition.split('=').slice(1).join('=')
              : '';
          a.download = `AI_Search_${generateFileName(filenamePart)}`;
          a.click();
          window.URL.revokeObjectURL(url);
      } catch {
          toastr.error('Failed to process the file');
      }
  };

  // ========================================
  // QUERY HANDLERS
  // ========================================
  
  const applyQueryResponse = (query, responseData, options = {}) => {
      if (responseData.message) {
          setErrorMessage(responseData.message);
          setTableHeaderData([]);
          setRowData([]);
          return;
      }

      const rowHeader = responseData.results?.row_header || [];
      const rowDataResult = responseData.results?.row_data || [];

      setTableHeaderData(rowHeader);
      setRowData(rowDataResult);

      if (rowDataResult.length === 0) {
          toastr.info('No Records Found', { timeOut: 10000 });
      }

      setAiSearchQuery((prev) => ({
          ...prev,
          user_query: query,
          generated_query: responseData.query,
          collection_name: responseData.collection,
          is_new_query: options.isNewQuery !== undefined ? options.isNewQuery : prev.is_new_query,
          query_name: options.queryName !== undefined ? options.queryName : prev.query_name,
          user_email: options.userEmail !== undefined ? options.userEmail : prev.user_email,
          timestamp: options.timestamp !== undefined ? options.timestamp : prev.timestamp,
          selected_chart_config: null,
          chart_suggestions: options.chartSuggestions !== undefined ? options.chartSuggestions : [],
      }));
  };

  const handleNewQuery = async (query) => {
      setRowData([]);
      setTableHeaderData([]);

      const [error, response] = await request('redirect/ai/query-mongo/', methods.POST, { user_query: query });

      if (error) {
          console.error('Error in new query:', error);
          setErrorMessage(error.message || 'An error occurred while processing your query');
          return;
      }

      applyQueryResponse(query, response.data, { isNewQuery: true, chartSuggestions: [] });
  };

  const handleRerunQuery = async (queryData) => {
      setRowData([]);
      setTableHeaderData([]);

      const [error, response] = await request('redirect/ai/rerun-query/', methods.POST, {
          user_query: queryData.user_query,
          generated_query: queryData.generated_query,
          collection_name: queryData.collection_name,
          user_email: queryData.user_email,
          query_name: queryData.query_name,
          module_id: moduleID,
          chart_suggestions: queryData.chart_suggestions || [],
      });

      if (error) {
          console.error('Error in rerun query:', error);
          setErrorMessage(error.message || 'An error occurred while processing your query');
          setTableHeaderData([]);
          setRowData([]);
          return;
      }

      applyQueryResponse(queryData.user_query, response.data, {
          isNewQuery: false,
          queryName: queryData.query_name,
          userEmail: queryData.user_email,
          timestamp: queryData.timestamp,
          chartSuggestions: queryData.chart_suggestions || [],
      });
  };

  // ========================================
  // CHART HANDLERS
  // ========================================
  
  const handleSuggestCharts = async () => {
      if (selectedPreviousQuery?.chart_suggestions?.length > 0) {
          setAiSearchQuery((prev) => ({
              ...prev,
              chart_suggestions: selectedPreviousQuery.chart_suggestions,
          }));
          return;
      }

      setChartLoading(true);

      try {
          const [error, response] = await request('redirect/ai/suggest-charts/', methods.POST, {
              generated_query: aiSearchQuery.generated_query,
              collection_name: aiSearchQuery.collection_name,
              user_query: aiSearchQuery.user_query,
          });

          if (error) {
              toastr.error(error.message || 'Failed to suggest charts');
              return;
          }

          const chartConfigs = response?.data?.chartConfigs || [];
          if (chartConfigs.length > 0) {
              setAiSearchQuery((prev) => ({
                  ...prev,
                  chart_suggestions: chartConfigs,
              }));
          } else {
              toastr.info('Unable to identify chart options for the given data', {
                  timeOut: 10000,
              });
          }
      } finally {
          setChartLoading(false);
      }
  };

  const handleSelectChart = (chart) => {
      setAiSearchQuery(prev => ({
          ...prev,
          selected_chart_config: chart
      }));
  };

  // ========================================
  // UI EVENT HANDLERS
  // ========================================
  
  const handleSend = async () => {
      if (aiSearchQuery.user_query.length > 0) {
          setErrorMessage("");
          setLoading(true);
          
          if (selectedPreviousQuery && aiSearchQuery.user_query === selectedPreviousQuery.user_query) {
              await handleRerunQuery(selectedPreviousQuery);
          } else {
              await handleNewQuery(aiSearchQuery.user_query);
          }
          
          setLoading(false);
          setTimeout(() => {
              if (aitableRef.current) {
                  aitableRef.current.scrollIntoView({ behavior: 'smooth' });
              }
          }, 200);
      } else {
          toastr.info("Please Enter Search Query", { timeOut: 2000 });
      }
  };

  const handleSaveClick = async () => {
      // Determine if this is an update scenario
      const isUpdateMode = !aiSearchQuery.is_new_query && selectedPreviousQuery;
      
      if (isUpdateMode) {
          // For updates, we need to ensure we have the existing query name
          if (!selectedPreviousQuery.query_name) {
              toastr.error('Cannot update query: No existing query name found');
              return;
          }
          
          // Directly send the update API call without opening the modal
          await handleDirectUpdate();
      } else {
          // For new saves, open the modal
          setSaveDialogOpen(true);
      }
  };

  const handleDirectUpdate = async () => {
      let userEmail = '';
      try {
          const userInfo = JSON.parse(localStorage.loggedInUser || '{}');
          userEmail = userInfo?.email || '';
      } catch {
          userEmail = '';
      }

      if (!userEmail) {
          toastr.error('User email not found. Please log in again.');
          return;
      }

      const payload = {
          user_query: aiSearchQuery.user_query,
          generated_query: aiSearchQuery.generated_query,
          collection_name: aiSearchQuery.collection_name,
          user_email: userEmail,
          query_name: selectedPreviousQuery.query_name,
          module_id: moduleID,
          chart_suggestions: aiSearchQuery.chart_suggestions || [],
          is_update: true,
      };

      const [error, response] = await request('redirect/ai/log-query/', methods.POST, payload);

      if (error) {
          if (error.status === 404) {
              toastr.error('Query not found. It may have been deleted.');
          } else if (error.status === 400) {
              toastr.error(error.message || 'No changes were made to the query.');
          } else {
              console.error('Error updating query:', error);
              toastr.error(error.message || 'Failed to update query');
          }
          return;
      }

      if (response) {
          toastr.success('Query updated successfully');
          fetchPreviousQueries();
      }
  };

  const handleHistoryClick = () => {
      setHistoryDialogOpen(true);
  };

  const handleQuerySelect = async (selectedQuery) => {
      console.log('Received Query', selectedQuery);
      // setSearchInput(selectedQuery.user_query);
      setAiSearchQuery(prev => ({
          ...prev,
          user_query: selectedQuery.user_query,
          is_new_query: false,
      }));
      setSelectedPreviousQuery(selectedQuery);
      setLoading(true);
      await handleRerunQuery(selectedQuery)
      setLoading(false);
  };

  // ========================================
  // EFFECTS
  // ========================================


  useEffect(() => {
      fetchPreviousQueries();
  }, []);

  // Compute save button visibility and tooltip
  useEffect(() => {
      const isVisible = rowData.length > 0 && (
          aiSearchQuery.is_new_query || 
          (selectedPreviousQuery && (!selectedPreviousQuery.chart_suggestions || selectedPreviousQuery.chart_suggestions.length === 0))
      );
      
      const tooltipText = !aiSearchQuery.is_new_query && 
          (!selectedPreviousQuery?.chart_suggestions || selectedPreviousQuery.chart_suggestions.length === 0) 
          ? "Update Query" 
          : "Save Query";
      
      setSaveButtonVisible(isVisible);
      setSaveButtonTooltip(tooltipText);
  }, [rowData.length, aiSearchQuery.is_new_query, selectedPreviousQuery]);

  // ========================================
  // RENDER
  // ========================================
  
  const headerData = changeKeysOfHeader(tableHeaderData);

  return (
      <div className={`ai-search ${rowData.length > 0 ? 'subsequent' : 'first'}`}>
          <Box className="ai-search-toolbar">
              <Typography variant="subtitle1" mt={1} sx={{ textAlign: "left" }}>
                  AI powered search: Access Optimus Insights
              </Typography>

              <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <TextField
                      variant="outlined"
                      placeholder="Enter Search Query and Click Send"
                      size="small"
                      fullWidth
                      sx={{ backgroundColor: "white" }}
                      InputProps={{
                          startAdornment: <SearchIcon sx={{ color: "gray", mr: 1 }} />,
                      }}
                      value={aiSearchQuery.user_query}
                      onChange={(e) => {
                          // setSearchInput(e.target.value);
                          setAiSearchQuery(prev => ({
                              ...prev,
                              user_query: e.target.value,
                          }));
                      }}
                      multiline
                  />
                  <Button
                      variant="contained"
                      startIcon={<SearchIcon sx={{ width: 18, height: 18 }} />}
                      sx={{
                          background: "linear-gradient(90deg, #2835C9 0%, #EC691F 100%)",
                          color: "white",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          minWidth: "auto",
                          "& .MuiButton-startIcon": {
                              marginRight: "2px",
                              marginLeft: "0px"
                          },
                          "&:hover": {
                              background: "linear-gradient(90deg, #2835c9 0%, #EC691F 100%)",
                          },
                      }}
                      onClick={handleSend}
                  >
                      Ask EPM
                  </Button>
                  {saveButtonVisible && (
                      <Tooltip title={saveButtonTooltip} arrow>
                          <IconButton
                              disabled={loading}
                              size="small"
                              onClick={handleSaveClick}
                          >
                              <SaveAsIcon sx={{ color: "#0F9D58" }} />
                          </IconButton>
                      </Tooltip>
                  )}
                  <Tooltip title="View Previous Queries" arrow>
                      <IconButton
                          size="small"
                          onClick={handleHistoryClick}
                          disabled={isFetchingQueries}
                      >
                          {isFetchingQueries ? (
                              <CircularProgress size={20} />
                          ) : (
                              <HistoryIcon sx={{ color: "#285CB1" }} />
                          )}
                      </IconButton>
                  </Tooltip>
              </Box>
              
              {loading && (
                  <Box
                      className="box-center text-center"
                      sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                      }}
                  >
                      Searching <CircularProgress size={"25px"} />
                  </Box>
              )}
              
              {errorMessage && (
                  <Box sx={{ wordWrap: "break-word", mt: 1 }}>{errorMessage}</Box>
              )}
          </Box>

          <div className="ai-search-results table-data-master table-region" ref={aitableRef}>
              {rowData.length > 0 && (
                  <>
                      <Box className="ai-search-actions">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              {aiSearchQuery.chart_suggestions && aiSearchQuery.chart_suggestions.length > 0 ? (
                                  <>
                                      <Typography variant="subtitle2">
                                          Chart Options:
                                      </Typography>
                                      {aiSearchQuery.chart_suggestions.map((chart, index) => (
                                          <Tooltip title={chart.title} arrow key={index}>
                                              <Button
                                                  variant="contained"
                                                  className="text-capitalize"
                                                  color="primary"
                                                  style={{ minWidth: "100px", fontSize: "12px", textTransform: "capitalize" }}
                                                  onClick={() => handleSelectChart(chart)}
                                              >
                                                  {chart.chartType}
                                              </Button>
                                          </Tooltip>
                                      ))}
                                  </>
                              ) : (
                                  <Button
                                      variant="contained"
                                      color="primary"
                                      style={{ minWidth: "100px", fontSize: "12px" }}
                                      onClick={handleSuggestCharts}
                                      disabled={chartLoading}
                                  >
                                      Suggest Charts
                                  </Button>
                              )}
                              {chartLoading && (
                                  <>
                                      <CircularProgress
                                          size={20}
                                          thickness={5}
                                          sx={{
                                              "& .MuiCircularProgress-circle": {
                                                  stroke: "url(#loader-gradient)",
                                              },
                                          }}
                                      />
                                      <svg width="0" height="0">
                                          <defs>
                                              <linearGradient
                                                  id="loader-gradient"
                                                  x1="1"
                                                  y1="0"
                                                  x2="0"
                                                  y2="1"
                                              >
                                                  <stop offset="0%" stopColor="#EC691F" />
                                                  <stop offset="100%" stopColor="#0160C6" />
                                              </linearGradient>
                                          </defs>
                                      </svg>
                                  </>
                              )}
                          </Box>

                          <Box className="icon-excel flex">
                              <Tooltip title="Download to Excel" arrow>
                                  <img
                                      alt=""
                                      src={APP_ICONS.downloadExcelIcon}
                                      className="cursor pointer"
                                      height="30"
                                      onClick={(event) => {
                                          event.preventDefault();
                                          handleDownloadExcel();
                                      }}
                                  />
                              </Tooltip>
                          </Box>
                      </Box>

                      {aiSearchQuery.selected_chart_config && (
                          <Box sx={{ mb: 2, textAlign: "center" }}>
                              <ChartRenderer chartConfig={aiSearchQuery.selected_chart_config} data={rowData} />
                          </Box>
                      )}

                      <div className="xpna-table-wrapper">
                          <TableComponent
                              columns={headerData}
                              data={rowData}
                              className="xpna-pure-table ai-search-pure-table"
                              hidePagination={false}
                              totalRecords={rowData.length}
                          />
                      </div>
                  </>
              )}
          </div>

          <SaveQueryDialog
              open={saveDialogOpen}
              onClose={() => setSaveDialogOpen(false)}
              query={aiSearchQuery.user_query}
              collectionName={aiSearchQuery.collection_name}
              dbQuery={aiSearchQuery.generated_query}
              previousQueries={previousQueries}
              fetchPreviousQueries={fetchPreviousQueries}
              chartSuggestions={aiSearchQuery.chart_suggestions || []}
              moduleID={moduleID}
          />
          <QueryHistoryDialog
              open={historyDialogOpen}
              onClose={() => setHistoryDialogOpen(false)}
              previousQueries={previousQueries}
              onSelectQuery={handleQuerySelect}
          />
      </div>
  );
};

export default AISearch;
// export default pageAccessCheck(AISearch, moduleID);
